import React, { MouseEvent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { Button, Checkbox, Collapse, Form, Input, InputRef } from 'antd';
import './MapSelectionPopup.css';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import ConditionalRender from 'app/common/components/ConditionalRender';

const { Panel } = Collapse;

export type MapSelectionValues = {
    [key: string]: MapSelectionValue;
};

interface MapSelectionPopupProps extends ModalPanelProps {
    values: MapSelectionValues;

    onSelect(values: MapSelectionValues);

    onNewLayer(values: MapSelectionValues, position: number[]);
}

export interface MapSelectionValue {
    label: string;
    options: Array<string>;
    selected: Array<string>;
}

interface Internal {
    checked: boolean;
    selectedItems: Array<string>;
}

type InternalValues = { [key: string]: Internal };

interface MapSelectionPopupState {
    activePanels: Array<string>;
    selection: InternalValues;
    isAllSelected: boolean;
}

/**
 * Generic popup which accepts a class with several properties.
 * Each property should be of type MapSelectionValue and it will be displayed as a group, where the user can select each individual option available in MapSelectionValue.options
 *
 */
export default class MapSelectionPopup extends React.PureComponent<MapSelectionPopupProps, MapSelectionPopupState> {
    constructor(props) {
        super(props);
        this.state = {
            activePanels: [],
            selection: {},
            isAllSelected: false,
        };
    }

    componentDidMount(): void {
        const values = this.isSingleElement(this.props.values) ? this.selectAll(this.props.values) : this.props.values;

        this.setState(
            {
                //expand all
                activePanels: Object.keys(this.props.values),
                selection: this.inputToInternal(values),
            },
            () => this.syncSelectAll(),
        );
    }

    isSingleElement(values: MapSelectionValues): boolean {
        if (Object.keys(values)?.length === 1) {
            const keys = Object.keys(values);
            return values[keys[0]].options.length === 1;
        }
        return false;
    }

    isSingleElementChecked(values: MapSelectionValues): boolean {
        if (Object.keys(values)?.length === 1) {
            const keys = Object.keys(values);
            //because selection would contain every item selected of the given type(key), its required to filter
            return values[keys[0]].selected.filter(selected => selected === values[keys[0]].options[0]).length === 1;
        }
        return false;
    }

    selectAll(values: MapSelectionValues): MapSelectionValues {
        const output: MapSelectionValues = {};
        for (const key of Object.keys(values)) {
            output[key] = Object.assign({}, values[key], { selected: values[key].options });
            // values[key].selected = values[key].options;
        }
        return output;
    }

    render() {
        return (
            <Flex
                direction="vertical"
                className="settings-content"
                style={{ width: '20em', padding: '1.5em', marginTop: '1em' }}>
                <span style={{ textAlign: 'center', paddingBottom: '2.5em' }}>Add to selection/layer</span>
                <ConditionalRender condition={!this.isSingleElement(this.props.values)}>
                    <Checkbox
                        checked={this.state.isAllSelected}
                        onChange={this.handleSelectAllChange}
                        style={{ paddingLeft: '1.45em' }}>
                        Select All
                    </Checkbox>
                    <Collapse
                        collapsible={'icon'}
                        activeKey={this.state?.activePanels}
                        onChange={this.handleActivePanelChange}>
                        {this.renderValues()}
                    </Collapse>
                </ConditionalRender>
                <Flex direction={'horizontal'} style={{ justifyContent: 'flex-end', gap: '1.5em' }} className="buttons">
                    <ConditionalRender condition={!this.isSingleElement(this.props.values)}>
                        <Button onClick={this.handleClickSelect}>SELECT</Button>
                    </ConditionalRender>
                    <ConditionalRender condition={this.isSingleElement(this.props.values)}>
                        <Button onClick={this.handleClickSelectSingleElement}>
                            {this.isSingleElementChecked(this.props.values) ? 'UNSELECT' : 'SELECT'}
                        </Button>
                    </ConditionalRender>
                    <Button onClick={this.handleClickCreateNewLayer}>CREATE LAYER</Button>
                </Flex>
            </Flex>
        );
    }

    renderValues() {
        const result = [];
        for (const key of Object.keys(this.props.values)) {
            result.push(this.renderGroup(key, this.props.values[key]));
        }
        return result;
    }

    renderGroup(groupName: string, input: MapSelectionValue) {
        return (
            <Panel key={groupName} header={this.renderGroupPanelHeader(groupName, input.label)}>
                <Flex direction={'vertical'}>
                    <Checkbox.Group
                        className="checkbox-group-flex-vertical group-items"
                        options={input.options}
                        value={this.state.selection[groupName]?.selectedItems}
                        onChange={list => this.handleCheckboxGroupItemChange(groupName, list)}
                    />
                </Flex>
            </Panel>
        );
    }

    renderGroupPanelHeader(groupName: string, label: string) {
        return (
            <Checkbox
                name={groupName}
                onChange={this.handleCheckboxGroupChange}
                checked={this.state.selection[groupName]?.checked}>
                {label}
            </Checkbox>
        );
    }

    handleCheckboxGroupChange = event => {
        this.setState(
            prevState => {
                return {
                    selection: Object.assign({}, prevState.selection, {
                        [event.target.name]: this.updateGroupSelection(
                            prevState.selection[event.target.name],
                            event.target.name,
                            event.target.checked,
                        ),
                    }),
                };
            },
            () => this.syncSelectAll(),
        );
    };

    private updateGroupSelection(prevValue: Internal, groupName: string, checked: boolean) {
        return Object.assign({}, prevValue, {
            checked: checked,
            selectedItems: checked ? this.props.values[groupName].options : [],
        });
    }

    private updateGroupItemSelection(prevValue: object, groupName: string, selectedItems: Array<string>) {
        return Object.assign({}, prevValue, {
            selectedItems: selectedItems,
            checked: this.props.values[groupName].options.length === selectedItems.length,
        });
    }

    handleCheckboxGroupItemChange = (groupName, list) => {
        this.setState(
            prevState => {
                return {
                    selection: Object.assign({}, prevState.selection, {
                        [groupName]: this.updateGroupItemSelection(prevState.selection[groupName], groupName, list),
                    }),
                };
            },
            () => this.syncSelectAll(),
        );
    };

    private syncSelectAll() {
        this.setState(prevState => {
            let allOptionsSelected = true;
            for (const key of Object.keys(this.props.values)) {
                if (this.props.values[key].options.length !== prevState.selection[key].selectedItems.length) {
                    allOptionsSelected = false;
                    break;
                }
            }
            return {
                isAllSelected: allOptionsSelected,
            };
        });
    }

    //handlers
    handleActivePanelChange = value => {
        this.setState({
            activePanels: value,
        });
    };

    handleSelectAllChange = event => {
        if (event.target.checked) {
            this.setState(prevState => {
                const newSelection = {};
                for (const groupName of Object.keys(this.props.values)) {
                    newSelection[groupName] = {
                        checked: true,
                        selectedItems: this.props.values[groupName].options,
                    };
                }
                return {
                    selection: newSelection,
                    isAllSelected: true,
                };
            });
        } else {
            this.setState({
                selection: {}, //clear object
                isAllSelected: false,
            });
        }
    };

    handleClickSelect = () => {
        const selectionOutput: MapSelectionValues = this.getSelectionOutput();
        this.props.onSelect(selectionOutput);
        this.props.onClose();
    };

    handleClickSelectSingleElement = () => {
        const selectionOutput: MapSelectionValues = this.getSelectionOutput();
        if (this.isSingleElementChecked(this.props.values)) {
            //if is select it, then unselect it
            Object.keys(selectionOutput).forEach(key => (selectionOutput[key].selected = []));
        }
        this.props.onSelect(selectionOutput);
        this.props.onClose();
    };

    handleClickCreateNewLayer = (event: MouseEvent) => {
        const selectionOutput: MapSelectionValues = this.getSelectionOutput();
        this.props.onClose();
        this.props.onNewLayer(selectionOutput, [event.clientX, event.clientY]);
    };

    private inputToInternal(inputValues: MapSelectionValues) {
        const internal: InternalValues = {};
        for (const key of Object.keys(inputValues)) {
            internal[key] = {
                selectedItems: inputValues[key].selected,
                checked: inputValues[key].selected.length === inputValues[key].options.length,
            };
        }
        return internal;
    }

    private getSelectionOutput() {
        const selectionOutput: MapSelectionValues = {};
        for (const key of Object.keys(this.props.values)) {
            //using props values to ensure that all properties are included in the select event object
            selectionOutput[key] = {
                options: this.props.values[key].options,
                label: null,
                selected: this.state.selection[key]?.selectedItems,
            };
        }
        return selectionOutput;
    }
}

interface NewLayerPopupProps extends ModalPanelProps {
    onSave: (name: string) => void;
}

interface NewLayerPopupState {
    name: string;
}

export class NewLayerPopup extends React.PureComponent<NewLayerPopupProps, NewLayerPopupState> {
    state = {
        name: '',
    };
    inputNameRef = React.createRef<InputRef>();

    componentDidMount(): void {
        this.inputNameRef.current.focus();
    }

    render() {
        return (
            <Flex className="share-solution" direction="vertical">
                <Flex className="header" direction="vertical" size={'3em'}>
                    <div className="title">New layer</div>
                </Flex>
                <Form layout="vertical">
                    <Form.Item className="solutions" label="Name">
                        <Input
                            ref={this.inputNameRef}
                            value={this.state.name}
                            onKeyDown={this.handleKeyDown}
                            onChange={event => this.setState({ name: event.target.value })}
                        />
                    </Form.Item>
                </Form>
                <Flex className="buttons">
                    <Button onClick={this.props.onClose}>Cancel</Button>
                    <Button onClick={this.handleOnSave}>Save</Button>
                </Flex>
            </Flex>
        );
    }

    handleKeyDown = event => {
        if (event.key === 'Enter') {
            this.handleOnSave();
        }
    };

    handleOnSave = () => {
        this.props.onClose();
        this.props.onSave(this.state.name);
    };
}
